import type {
  OpenQuestionAnswerEntry,
  OpenQuestionAnswersForItems,
} from '../../../../reducers/student/tracking/openQuestionAnswersReducer';
import type { TrackingIdentifier } from '@ael/core-api';
import { Map } from 'immutable';
import { InteractionId, InteractionResponse } from './InteractionCodecs';
import type { ScormClient } from './scormTyped';

export const storeInteractionsToLms12 = (answers: OpenQuestionAnswersForItems, scormClient: ScormClient): void => {
  if (answers.isEmpty()) return;

  try {
    const countStr = scormClient.get('cmi.interactions._count');
    let nextIndex = parseInt(countStr, 10) || 0;

    answers.forEach((widgetAnswers, contentItemTrackingId) => {
      widgetAnswers.forEach((entry, widgetTrackingId) => {
        const interactionId = InteractionId.encode(contentItemTrackingId, widgetTrackingId);
        const formattedResponse = InteractionResponse.encode(entry);

        scormClient.set(`cmi.interactions.${nextIndex}.id`, interactionId);
        scormClient.set(`cmi.interactions.${nextIndex}.type`, 'fill-in');
        scormClient.set(`cmi.interactions.${nextIndex}.student_response`, formattedResponse);
        nextIndex++;
      });
    });
  } catch (error) {
    console.error('Error storing interactions to LMS 1.2:', error);
  }
};

/**
 * Compares two sets of open question answers and returns only the entries that are new or have changed.
 * This is used to minimize the amount of data stored in SCORM 1.2 by only updating interactions that have actually changed.
 */
export const diffOpenQuestionAnswers = (
  newAnswers: OpenQuestionAnswersForItems,
  oldAnswers: OpenQuestionAnswersForItems
): OpenQuestionAnswersForItems => {
  let diff = Map<TrackingIdentifier, Map<TrackingIdentifier, OpenQuestionAnswerEntry>>();

  newAnswers.forEach((newWidgetAnswers, contentItemTrackingId) => {
    const oldWidgetAnswers = oldAnswers.get(contentItemTrackingId);
    let diffWidgetAnswers = Map<TrackingIdentifier, OpenQuestionAnswerEntry>();

    newWidgetAnswers.forEach((newEntry, widgetTrackingId) => {
      const oldEntry = oldWidgetAnswers?.get(widgetTrackingId);
      const isChanged =
        !oldEntry || oldEntry.answer !== newEntry.answer || oldEntry.questionText !== newEntry.questionText;

      if (isChanged) {
        diffWidgetAnswers = diffWidgetAnswers.set(widgetTrackingId, newEntry);
      }
    });

    if (!diffWidgetAnswers.isEmpty()) {
      diff = diff.set(contentItemTrackingId, diffWidgetAnswers);
    }
  });

  return diff;
};
